/**
 * Client Portal - My Documents
 * Read-only document viewing for external users (investors)
 * Documents are uploaded by internal staff, investors can only view and download
 */

import React, { useState, useEffect, useCallback } from 'react';
import useAsyncData from '../../hooks/useAsyncData';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import apiClient, { apiGet } from '../../utils/apiClient';
import logger from '../../utils/logger';
import { formatDate, formatFileSize } from '../../utils/formatters';
import ContractReferenceCell from '../../components/contracts/ContractReferenceCell';
import SortableHeaderCell from '../../components/common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';

const CLIENT_DOC_COLUMNS = [
  { key: 'file_name',          label: 'File Name', align: 'left', getValue: (d) => d.file_name },
  { key: 'contract_reference', label: 'Contract',  align: 'left', getValue: (d) => d.contract_reference },
  { key: 'document_type',      label: 'Type',      align: 'left', getValue: (d) => d.document_type },
  { key: 'file_size',          label: 'Size',      align: 'left', getValue: (d) => d.file_size != null ? Number(d.file_size) : null },
  { key: 'created_at',         label: 'Date',      align: 'left', getValue: (d) => d.created_at },
];

// Fallback document types (used if API fails to load)
const FALLBACK_DOCUMENT_TYPES = [
  { code: 'proof_of_payment', display_name: 'Proof of Payment' },
  { code: 'kyc', display_name: 'KYC Document' },
  { code: 'bank_statement', display_name: 'Bank Statement' },
  { code: 'contract', display_name: 'Contract Document' },
  { code: 'supporting_doc', display_name: 'Supporting Document' },
  { code: 'other', display_name: 'Other' },
];

const ClientDocuments = () => {
  const [documentTypes, setDocumentTypes] = useState(FALLBACK_DOCUMENT_TYPES);
  const sort = useTableSort();

  const { data: documents, loading, error, reload } = useAsyncData(
    async () => {
      const docsRes = await apiGet('/client-portal/my-documents');
      // Only show approved documents to clients
      return (docsRes.data || docsRes || []).filter(doc => doc.status === 'approved');
    },
    { defaultValue: [], extractData: false }
  );

  const loadDocumentTypes = useCallback(async () => {
    try {
      const response = await apiGet('/refdata/document-types/active');
      if (response.data && response.data.length > 0) {
        setDocumentTypes(response.data);
      }
    } catch (err) {
      logger.warn('Failed to load document types, using fallback', { message: err.message, status: err.response?.status });
    }
  }, []);

  useEffect(() => {
    loadDocumentTypes();
  }, [loadDocumentTypes]);

  const handleDownload = async (docId) => {
    try {
      const response = await apiClient.get(`/client-portal/my-documents/${docId}/download`, {
        responseType: 'blob',
      });
      const contentDisposition = response.headers?.['content-disposition'];
      const fileName = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : 'download';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Error downloading document', { message: err.message, status: err.response?.status });
    }
  };

  const getDocumentTypeLabel = (code) => {
    const found = documentTypes.find(t => t.code === code);
    return found?.display_name || code;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          My Documents
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={reload}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        Documents related to your investments are shown here. To submit new documents, please contact your account manager.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
            <TableHead>
              <TableRow>
                {CLIENT_DOC_COLUMNS.map((col) => (
                  <SortableHeaderCell key={col.key} sort={sort} columnKey={col.key} align={col.align}>
                    {col.label}
                  </SortableHeaderCell>
                ))}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box sx={{ py: 4 }}>
                      <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography color="text.secondary">
                        No documents available
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Documents will appear here once they have been uploaded and approved.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                sort.sortRows(documents, CLIENT_DOC_COLUMNS).map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">{doc.file_name}</Typography>
                      {doc.notes && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {doc.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell><ContractReferenceCell reference={doc.contract_reference || '-'} userReference={doc.user_reference} /></TableCell>
                    <TableCell>{getDocumentTypeLabel(doc.document_type)}</TableCell>
                    <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                    <TableCell>{formatDate(doc.created_at)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleDownload(doc.id)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

export default ClientDocuments;
